import { Dialog, DialogContent, DialogTitle, IconButton, TextField } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parse, format } from 'date-fns';
import React, { useState, useMemo, useEffect } from 'react';
import { EngagementPlanTask } from '@redux/tasks/tasksTypes';
import { Button } from 'components/Button/Button';
import { ExpirySelectBlock } from 'components/ExpirySelectBlock/ExpirySelectBlock';
import { FREQUENCY_SELECT_OPTIONS, FrequencySelectBlock } from 'components/FrequencySelectBlock/FrequencySelectBlock';
import { CloseIcon } from 'components/Icons';
import { TextArea } from 'components/TextArea/TextArea';
import { TriggerSelectBlock, TriggerTypes } from 'components/TriggerSelectBlock/TriggerSelectBlock';
import { Text } from 'components/Typography';
import { Color } from 'utils/color';
import { isTimeDuringSleep } from 'utils/form';
import { MAX_TASK_MESSAGE_LENGTH } from '../../../../configConsts';
import { customInputPlaceholderStyles, customDateTimePickerStyles, makeCustomInputStyles } from '../../../../customStyles';
import style from './AddTaskModal.scss';

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: (task: EngagementPlanTask) => void;
  duration: number;
};

const createDefaultTask = (duration: number): EngagementPlanTask => ({
  taskType: 'Messaging',
  taskName: '',
  frequencyType: FREQUENCY_SELECT_OPTIONS.no_repeat,
  endsAfterOccurrences: 1,
  deliveryStartTime: '09:00:00',
  deliveryExpirationTime: '21:00:00',
  deliverableMessage: '',
  expectedResponse: false,
  responseType: '',
  triggerCondition: TriggerTypes.inBed
});

export const AddTaskModal: React.FC<Props> = ({ open, onClose, onSave, duration }) => {
  const [task, setTask] = useState<EngagementPlanTask>(createDefaultTask(duration));
  const [deliveryStartTimeDate, setDeliveryStartTimeDate] = useState<Date>(parse('09:00:00', 'HH:mm:ss', new Date()));
  const [deliveryExpirationTimeDate, setDeliveryExpirationTimeDate] = useState<Date>(parse('21:00:00', 'HH:mm:ss', new Date()));
  const [triggerCondition, setTriggerCondition] = useState<TriggerTypes>(TriggerTypes.inBed);

  const customInputStyles = makeCustomInputStyles();

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setTask(createDefaultTask(duration));
      setDeliveryStartTimeDate(parse('09:00:00', 'HH:mm:ss', new Date()));
      setDeliveryExpirationTimeDate(parse('21:00:00', 'HH:mm:ss', new Date()));
      setTriggerCondition(TriggerTypes.inBed);
    }
  }, [open, duration]);

  const errors = useMemo(() => {
    const isInvalidDeliveryTime = !deliveryStartTimeDate || isNaN(deliveryStartTimeDate.getTime());
    const isInvalidExpiryTime = !deliveryExpirationTimeDate || isNaN(deliveryExpirationTimeDate.getTime());

    return {
      taskName: task.taskName.trim() ? '' : 'Task name is required',
      message: task.deliverableMessage.trim() ? '' : 'Message is required',
      dateTime: isInvalidDeliveryTime ? 'Please enter a valid time' : isTimeDuringSleep(deliveryStartTimeDate) ? 'Task time must be between 5AM and 12AM' : '',
      expiredDateTime: isInvalidExpiryTime ? 'Please select a valid expiration time' : ''
    };
  }, [task, deliveryStartTimeDate, deliveryExpirationTimeDate]);

  const isValidForm = !Object.values(errors).filter(Boolean).length;

  const handleSave = () => {
    if (isValidForm) {
      const taskToSave = {
        ...task,
        deliveryStartTime: format(deliveryStartTimeDate, 'HH:mm:ss'),
        deliveryExpirationTime: format(deliveryExpirationTimeDate, 'HH:mm:ss'),
        endsAfterOccurrences: task.frequencyType === FREQUENCY_SELECT_OPTIONS.every_day ? duration : 1,
        triggerCondition: triggerCondition
      };
      onSave(taskToSave);
      handleClose();
    }
  };

  const handleClose = () => {
    setTask(createDefaultTask(duration));
    setDeliveryStartTimeDate(parse('09:00:00', 'HH:mm:ss', new Date()));
    setDeliveryExpirationTimeDate(parse('21:00:00', 'HH:mm:ss', new Date()));
    setTriggerCondition(TriggerTypes.inBed);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle className={style.dialogTitle}>
        <Text variant="h5" weight={500}>
          Add New Task
        </Text>
        <IconButton onClick={handleClose} className={style.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={style.dialogContent}>
        <div className={style.fieldWrapper}>
          <Text variant="sm" color={Color.GRAY} className={style.fieldLabel}>
            Task Name *
          </Text>
          <TextField
            variant="outlined"
            value={task.taskName}
            onChange={(e) => setTask((prev) => ({ ...prev, taskName: e.target.value }))}
            className={customInputStyles.input}
            sx={customInputPlaceholderStyles}
            error={!!errors.taskName}
            placeholder="Enter task name"
          />
          {errors.taskName && (
            <Text variant="sm" className={style.error}>
              {errors.taskName}
            </Text>
          )}
        </div>

        <div className={style.fieldWrapper}>
          <Text variant="sm" color={Color.GRAY} className={style.fieldLabel}>
            Message *
          </Text>
          <TextArea
            rows={4}
            maxLength={MAX_TASK_MESSAGE_LENGTH}
            value={task.deliverableMessage}
            onChange={(e) => setTask((prev) => ({ ...prev, deliverableMessage: e.target.value }))}
            placeholder="Enter the message for this task"
            error={errors.message}
          />
        </div>

        <div className={style.multiConditionLine}>
          <div className={style.conditionHolder}>
            <Text variant="nlg" className={style.conditionTitle}>
              Deliver at
            </Text>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <TimePicker
                value={deliveryStartTimeDate}
                className={style.timePicker}
                onChange={(date) => {
                  if (date) {
                    setDeliveryStartTimeDate(date);
                  }
                }}
                shouldDisableTime={(timeValue) => isTimeDuringSleep(timeValue)}
                slotProps={{
                  textField: {
                    error: !!errors.dateTime
                  }
                }}
                sx={customDateTimePickerStyles}
              />
            </LocalizationProvider>
            {errors.dateTime && (
              <Text variant="sm" className={style.error}>
                {errors.dateTime}
              </Text>
            )}
          </div>
          <div className={style.conditionHolder}>
            <ExpirySelectBlock
              selectedTime={deliveryStartTimeDate}
              expiredTime={deliveryExpirationTimeDate}
              onExpiredTimeSelected={(date) => {
                if (date) {
                  setDeliveryExpirationTimeDate(date as Date);
                }
              }}
              error={errors.expiredDateTime}
            />
          </div>
        </div>

        <TriggerSelectBlock trigger={triggerCondition} onTriggerChanged={setTriggerCondition} />

        <FrequencySelectBlock
          frequency={{ repeat: task.frequencyType as FREQUENCY_SELECT_OPTIONS }}
          allowedFrequencyTypes={[FREQUENCY_SELECT_OPTIONS.every_day, FREQUENCY_SELECT_OPTIONS.no_repeat]}
          showFrequencyDetails={false}
          onFrequencyChanged={(value: any) =>
            setTask((prev) => ({
              ...prev,
              frequencyType: value.repeat,
              endsAfterOccurrences: value.repeat === FREQUENCY_SELECT_OPTIONS.every_day ? duration : 1
            }))
          }
          errors={{}}
        />

        <div className={style.buttonContainer}>
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!isValidForm}>
            Add Task
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
